"use client";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@shared-utils/ui";
import Link from "next/link";
import { Tooltip, TooltipContent, TooltipTrigger } from "./shadcn/tooltip";

const listItemVariants = cva(
  "last:border-b border-border border-t text-left w-full block items-center group  text-xs transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
  {
    variants: {
      size: {
        default: "h-8 px-2 py-2",
        sm: "h-6 px-2 py-1",
      },
      variant: {
        default: "hover:bg-accent",
        sand: "hover:bg-panel-100 bg-panel-200",
        light:
          "border-none rounded-md hover:bg-accent text-foreground py-0 cursor-pointer flex flex-row items-center gap-1 group",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof listItemVariants> {
  asChild?: boolean;
  selected?: boolean;
  hoverActions?: React.ReactNode;
  hoverActionsContainerClassName?: string;
}

const ListItem = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      hoverActions,
      hoverActionsContainerClassName,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          listItemVariants({ variant, size, className }),
          {
            "bg-ter-blue-200 hover:bg-ter-blue-200  text-foreground border-l-2 border-l-ter-green-400":
              props.selected,
          },
          className,
        )}
        ref={ref}
        {...props}
      >
        {hoverActions ? (
          <div className="flex flex-1 flex-row  items-center min-w-0">
            {children}
            {hoverActions ? (
              <div
                className={cn(
                  "hidden group-hover:opacity-100 md:hidden group-hover:flex md:opacity-0  flex-row",
                  hoverActionsContainerClassName,
                )}
              >
                {hoverActions}
              </div>
            ) : null}
          </div>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
ListItem.displayName = "ListItem";

export { ListItem, listItemVariants };

interface ListItemHoverActionProps {
  className?: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  href?: string;
  preventDefault?: boolean;
  tooltip?: string;
}
export const ListItemHoverAction = (props: ListItemHoverActionProps) => {
  const className = cn(
    "block p-1 w-auto cursor-pointer hover:bg-background/50 rounded-md",
    props.className,
  );
  const content = props.href ? (
    <Link
      href={props.href}
      className={className}
      onClick={(e) => {
        e.stopPropagation();
        if (!props.onClick) return;
        props.onClick?.(e);
      }}
    >
      {props.children}
    </Link>
  ) : (
    <div
      role="button"
      tabIndex={0}
      aria-label={props.tooltip}
      className={className}
      onClick={(e) => {
        if (props.preventDefault ?? true) {
          e.preventDefault();
        }
        e.stopPropagation();
        if (!props.onClick) return;
        props.onClick?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        if (props.preventDefault ?? true) {
          e.preventDefault();
        }
        e.stopPropagation();
        props.onClick?.(e);
      }}
    >
      {props.children}
    </div>
  );

  if (!props.tooltip) return content;

  return (
    <Tooltip disableHoverableContent={true} delayDuration={150}>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent side="top">{props.tooltip}</TooltipContent>
    </Tooltip>
  );
};
